import React from "react";
import EcoBonusPromotion from "../../../../../../common-deprecated/components/aem/lexus/EcoBonusPromotion";
import AvailabilityBadges from "../../../../../../common-deprecated/components/aem/lexus/availability-badges/AvailabilityBadges";
import ButtonPrimary from "../../../../../../common-deprecated/components/aem/lexus/button/ButtonPrimary";
import type { CommonSettingsType } from "../../../../../../common-deprecated/settings/fetchCommonSettings";
import { getSubmodelGradeImage } from "../../../../../../common-deprecated/settings/utils/ccisSettingUtils";
import ImageFluid from "../../../../../../common-deprecated/styles/v2/lexus/globals/Image";
import { Ellipsis } from "../../../../../../common-deprecated/styles/v2/lexus/utilities/Ellipsis";
import type {
    ModelType,
    PricePromotionType,
    PromotionType,
} from "../../../../../../common-deprecated/types/CommonTypes";
import type { SubModelType } from "../../../../../../common-deprecated/types/GradeType";
import { getEcoBonusPromotions } from "../../../../../../common-deprecated/utils/promotionUtils";
import useStepUrl from "../../../hooks/shared/actions/useStepUrl";
import { NavStep } from "../../../../shared-logic/utils/Navigation";
import { BnbLabel } from "../../../utils/labels";
import { trackNavGoToStep } from "../../../utils/tracking";
import Price from "../common/price/Price";
import * as Styles from "./styles/Submodel";

type SubModelItemProps = {
    commonSettings: CommonSettingsType;
    model: ModelType;
    submodel: SubModelType;
    availablePromotions: PromotionType[];
    genericPriceDisclaimerSubmodel: string | null;
};

const SubModelItem = ({
    commonSettings,
    model,
    submodel,
    availablePromotions,
    genericPriceDisclaimerSubmodel,
}: SubModelItemProps): JSX.Element => {
    const imageUrl = getSubmodelGradeImage(commonSettings, 4, model.id, submodel.id);
    const ecoBonus = getEcoBonusPromotions<PricePromotionType>(availablePromotions, submodel.price.promotions);
    const submodelGrade = model.grades.find(({ defaultCarId }) => submodel.defaultCarId === defaultCarId);

    const submodelIsSoldOut = submodelGrade?.motorizations.every(({ soldOut }) => soldOut);
    const submodelIsAvailableForOnlineSale = !!submodelGrade?.availableForOnlineSales;
    const submodelIsAvailableForOnlineLeasing = !!submodelGrade?.availableForOnlineLeasing;

    const gradeStep = useStepUrl(
        NavStep.Grades,
        {
            modelId: model.id,
            subModelId: submodel.id,
            bodyTypeId: submodel.bodyType.id,
        },
        undefined,
        () => trackNavGoToStep(NavStep.Submodels, NavStep.Grades),
    );

    return (
        <Styles.Column width={{ _: 1, md: 1 / 2 }}>
            <Styles.Wrapper>
                <Styles.SubmodelImageWrapper>
                    <ImageFluid src={imageUrl} alt={submodel.name} />
                </Styles.SubmodelImageWrapper>
                <Styles.SubmodelWrapper>
                    <Styles.Header>
                        <Styles.SubmodelTitle>{submodel.name}</Styles.SubmodelTitle>
                        {/* This has been commented out because of missing engine information in texus submodel - OR-4870 */}
                        {/* TODO: add the actual engine when data gets added to the submodel endpoint */}
                        {/* <Styles.EngineLabel>
                                            Engine
                                        </Styles.EngineLabel> */}
                    </Styles.Header>
                    <Styles.Price>
                        <EcoBonusPromotion ecoBonus={ecoBonus} />
                        <Price
                            price={submodel.price}
                            financeInfo={submodel.finance}
                            carInternalCode={model.internalCode}
                            cashPriceDisclaimer={genericPriceDisclaimerSubmodel}
                        />
                    </Styles.Price>
                    <AvailabilityBadges
                        isSoldOut={submodelIsSoldOut}
                        isAvailableForOnlineSales={submodelIsAvailableForOnlineSale}
                        isAvailableForOnlineLeasing={submodelIsAvailableForOnlineLeasing}
                    />
                    {submodel.description && (
                        <Styles.Description>
                            <span dangerouslySetInnerHTML={{ __html: submodel.description }} />
                        </Styles.Description>
                    )}
                    <Styles.SubmodelButtonWrapper>
                        <ButtonPrimary fullWidth href={gradeStep.href} onClick={gradeStep.onClick}>
                            <Ellipsis maxWidth="100%" className="t-base-text l-micro-text">
                                <BnbLabel label="selectSubmodel" />
                            </Ellipsis>
                        </ButtonPrimary>
                    </Styles.SubmodelButtonWrapper>
                </Styles.SubmodelWrapper>
            </Styles.Wrapper>
        </Styles.Column>
    );
};

export default SubModelItem;
